import React from 'react';
import { Dimensions, Platform, StyleSheet, View } from 'react-native';

import { BigButton, Button } from '@components/Button';
import Carousel, { ICarouselInstance } from '@components/Carousel';
import Image, { ImageProps } from '@components/Image';
import ScreenContainer from '@components/ScreenContainer';
import { Paragraph, PrimaryHeadline, Title } from '@components/Typography';
import { useAppTheme } from '@context/ThemeContext';
import { OnboardingRoute } from '@navigation/Routes';
import { WelcomeScreenProps } from '@navigation/Types';

type DataItem = {
  imageSource: ImageProps['source'];
  caption: string;
};

export default function WelcomeScreen({ navigation }: WelcomeScreenProps) {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const carouselRef = React.useRef<ICarouselInstance>(null);
  const appTheme = useAppTheme();

  const data: DataItem[] = [
    { imageSource: require('../assets/images/add-plan.png'), caption: 'Share Your Plans' },
    { imageSource: require('../assets/images/add-people.png'), caption: 'Invite Your Friends' },
    {
      imageSource: require('../assets/images/add-group.png'),
      caption: 'See Where Your Friends Are Traveling',
    },
  ];

  const renderItem = ({ item }: { item: DataItem }) => {
    return (
      <View style={styles.itemContainer}>
        <Title style={styles.subheading}>{item.caption}</Title>
        <Image
          source={item.imageSource}
          style={styles.image}
          disablePlaceholder
          contentFit="contain"
        />
      </View>
    );
  };

  const carouselWidth = Math.min(Dimensions.get('window').width, appTheme.maxWidth);
  const carouselHeight = Dimensions.get('window').height * 0.7;
  const carouselMaxIndex = data.length - 1;

  const next = () => carouselRef?.current?.next();
  const atEnd = () => currentIndex >= carouselMaxIndex;

  const onButtonPress = () => {
    if (!atEnd()) {
      next();
      setCurrentIndex((curr) => Math.min(curr + 1, carouselMaxIndex));
    } else {
      close();
    }
  };

  const close = () => {
    navigation.navigate(OnboardingRoute.RequestPermissions);
  };

  const renderButton = () => (
    <BigButton
      mode="contained"
      onPress={onButtonPress}
      style={styles.button}
      testID="screens.WelcomeScreen.Button.Continue">
      {atEnd() ? 'Done' : 'Continue'}
    </BigButton>
  );

  return (
    <ScreenContainer>
      <Button
        style={styles.skipButton}
        labelStyle={{ color: appTheme.colors.placeholder }}
        onPress={close}
        mode="text"
        uppercase={false}
        compact
        testID="screens.WelcomeScreen.Button.Skip">
        Skip
      </Button>
      <PrimaryHeadline style={styles.headline}>Welcome to Joinstar</PrimaryHeadline>
      <View style={styles.carouselContainer}>
        <Carousel
          ref={carouselRef}
          data={data}
          renderItem={renderItem}
          onSnapToItem={(index) => setCurrentIndex(index)}
          width={carouselWidth}
          height={Platform.select({ default: undefined, web: carouselHeight })}
          autoPlay={false}
          loop={false}
        />
      </View>
      <Paragraph style={styles.paragraph}>{currentIndex + 1 + ' / ' + data.length}</Paragraph>
      {renderButton()}
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  button: {
    width: '90%',
  },
  carouselContainer: {
    height: '70%',
  },
  headline: {
    textAlign: 'center',
  },
  image: {
    alignSelf: 'center',
    flex: 1,
    flexGrow: 1,
    width: '80%',
  },
  itemContainer: {
    flex: 1,
    flexGrow: 1,
  },
  paragraph: {
    padding: 10,
    textAlign: 'center',
  },
  skipButton: {
    position: 'absolute',
    right: 5,
    top: 5,
  },
  subheading: {
    padding: 10,
    paddingHorizontal: 50,
    textAlign: 'center',
  },
});
